import { AxiosError } from 'axios';
import axiosInstance from '../utils/axios';

/**
 * Certificates (monetization feature #04).
 *
 * Two calls with deliberately different shapes, mirroring the server: issuing returns the
 * owner's view, verifying returns the stranger's. They are not the same payload and are
 * not interchangeable.
 *
 * Issuing distinguishes "this attempt has not earned one yet" (409) from a real failure.
 * A student who has not finished the paper is not an error state.
 */

export interface IssuedCertificate {
  certificateId: string;
  sessionId: string;
  hash: string;
  /** Path the QR code should encode; the client prefixes its own origin. */
  verifyPath: string;
  studentName: string | null;
  series: string | null;
  score: number | null;
  totalScore: number | null;
  percentage: number | null;
  issuedAt: string;
}

/** The public payload — no studentId, no phone, no sessionId. */
export interface CertificateVerification {
  valid: boolean;
  status: 'valid' | 'revoked' | 'not_found';
  studentName: string | null;
  series: string | null;
  score: number | null;
  totalScore: number | null;
  percentage: number | null;
  issuedAt: string | null;
}

export type IssueResult =
  | { state: 'ok'; certificate: IssuedCertificate }
  /** The session exists but has not been completed — nothing to certify yet. */
  | { state: 'not-earned'; message: string }
  | { state: 'not-found' }
  | { state: 'error'; message: string };

export const issueCertificate = async (sessionId: string): Promise<IssueResult> => {
  try {
    const response = await axiosInstance.post(`/certificates/${sessionId}`);
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', certificate: response.data.data as IssuedCertificate };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const error = err as AxiosError<{ message?: string }>;
    const status = error?.response?.status;
    if (status === 409) {
      return {
        state: 'not-earned',
        message:
          error.response?.data?.message ??
          'This attempt has not been completed, so there is no certificate yet.'
      };
    }
    if (status === 404) return { state: 'not-found' };
    return { state: 'error', message: 'Could not issue the certificate. Please try again.' };
  }
};

/**
 * Public verification. The server answers 200 even for an unknown code — deliberately, so
 * someone probing it cannot tell "no such certificate" from "revoked" by the status alone.
 * Read `status`, never the HTTP code.
 */
export const verifyCertificate = async (
  hash: string
): Promise<CertificateVerification | null> => {
  try {
    const response = await axiosInstance.get(`/verify/${hash}`);
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as CertificateVerification;
    }
    return null;
  } catch {
    return null;
  }
};
